import {
  Button,
  cn,
  Dialog,
  Draggable,
  Switch,
  Text,
} from '@risksmart-app/atomic-ui';
import { useCallback, useEffect, useState } from 'react';

import type {
  PreferencesDialogProps,
  PreferencesOutput,
  PreferencesSection,
} from './types';
import { reorderSections, toggleSectionEnabled } from './utils';

const PreferencesDialog = ({
  lang,
  state,
  actions,
}: PreferencesDialogProps) => {
  const { open, sections: initialSections } = state;
  const { onOpenChange, onSave } = actions;

  const [sections, setSections] = useState<PreferencesSection[]>([]);

  // Re-initialize internal state when dialog opens
  useEffect(() => {
    if (open) {
      setSections(initialSections.map((s) => ({ ...s })));
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open]);

  const sectionIds = sections.map((s) => s.id);

  const handleReorder = useCallback((newOrder: (string | number)[]) => {
    setSections((prev) => reorderSections(prev, newOrder));
  }, []);

  const toggleSection = useCallback((sectionId: string, enabled: boolean) => {
    setSections((prev) => toggleSectionEnabled(prev, sectionId, enabled));
  }, []);

  const handleSave = () => {
    const output: PreferencesOutput = { sections };
    onSave(output);
  };

  const handleCancel = () => {
    onOpenChange(false);
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange} size={'md'}>
      <Dialog.Header title={lang.title} description={lang.description} />
      <Dialog.Body>
        <Draggable
          items={sectionIds}
          onReorder={handleReorder}
          className={cn('flex flex-col gap-3')}
        >
          {sections.map((section) => (
            <Draggable.Item key={section.id} id={section.id}>
              <div
                className={cn(
                  'flex items-center gap-3 px-4 py-3 rounded-lg border border-neutral-border bg-neutral'
                )}
              >
                <Draggable.DragHandle />
                <Text preset={'body-md'} className={cn('flex-1')}>
                  {section.label}
                </Text>
                <Switch
                  checked={section.enabled}
                  onCheckedChange={(checked) =>
                    toggleSection(section.id, checked)
                  }
                  size={'md'}
                  aria-label={section.label}
                />
              </div>
            </Draggable.Item>
          ))}
          <Draggable.Overlay>
            {(activeId) => {
              const section = sections.find((s) => s.id === activeId);

              return (
                <div
                  className={cn(
                    'flex items-center gap-3 px-4 py-3 rounded-lg border border-neutral-border bg-neutral shadow-lg'
                  )}
                >
                  <Draggable.DragHandle />
                  <Text preset={'body-md'} className={cn('flex-1')}>
                    {section?.label}
                  </Text>
                  <Switch
                    checked={section?.enabled ?? true}
                    size={'md'}
                    disabled
                    aria-label={section?.label}
                  />
                </div>
              );
            }}
          </Draggable.Overlay>
        </Draggable>
      </Dialog.Body>
      <Dialog.Footer className={cn('flex gap-2')}>
        <Button variant={'default'} onClick={handleSave}>
          {lang.save}
        </Button>
        <Button variant={'neutral'} style={'outline'} onClick={handleCancel}>
          {lang.cancel}
        </Button>
      </Dialog.Footer>
    </Dialog>
  );
};

export { PreferencesDialog };
export type {
  PreferencesDialogActions,
  PreferencesDialogLang,
  PreferencesDialogProps,
  PreferencesDialogState,
  PreferencesOutput,
  PreferencesSection,
} from './types';
